import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, orderBy, query, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Plus, Trash2, Pencil, X, Check } from 'lucide-react';

const FlopManagement = () => {
  const [flops, setFlops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    fetchFlops();
  }, []);

  const fetchFlops = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'flops'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      setFlops(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching flops:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFlop = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try {
      await addDoc(collection(db, 'flops'), {
        name: name.trim(),
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setName('');
      fetchFlops();
    } catch (error) {
      console.error('Error adding flop:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteFlop = async (id) => {
    if (!window.confirm('Delete this flop?')) return;
    try {
      await deleteDoc(doc(db, 'flops', id));
      setFlops(flops.filter(f => f.id !== id));
    } catch (error) {
      console.error('Error deleting flop:', error);
    }
  };

  const handleEditFlop = (flop) => {
    setEditingId(flop.id);
    setEditName(flop.name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleSaveEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await updateDoc(doc(db, 'flops', id), {
        name: editName.trim(),
        updatedAt: new Date(),
      });
      setEditingId(null);
      setEditName('');
      fetchFlops();
    } catch (error) {
      console.error('Error updating flop:', error);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Flops</h2>
      <form onSubmit={handleAddFlop} className="flex flex-col sm:flex-row gap-2 mb-6">
        <input
          type="text"
          placeholder="Flop Name"
          value={name}
          onChange={e => setName(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
        <button
          type="submit"
          disabled={adding}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <Plus size={16} /> Add
        </button>
      </form>
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : flops.length === 0 ? (
        <div className="text-gray-500 text-center py-8">No flops found.</div>
      ) : (
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {flops.map(flop => (
              <tr key={flop.id}>
                <td className="px-4 py-2 text-gray-900">
                  {editingId === flop.id ? (
                    <input
                      type="text"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  ) : (
                    flop.name
                  )}
                </td>
                <td className="px-4 py-2 flex gap-2 items-center">
                  {editingId === flop.id ? (
                    <>
                      <button
                        onClick={() => handleSaveEdit(flop.id)}
                        className="text-green-600 hover:text-green-900 flex items-center gap-1"
                        title="Save"
                      >
                        <Check size={16} /> Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
                        title="Cancel"
                      >
                        <X size={16} /> Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEditFlop(flop)}
                        className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                        title="Edit"
                      >
                        <Pencil size={16} /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteFlop(flop.id)}
                        className="text-red-600 hover:text-red-900 flex items-center gap-1"
                        title="Delete"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default FlopManagement; 